import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, User, LogOut, Settings, Sun, Moon, Home, LayoutDashboard, Mic, BookOpen, ShoppingBag, GraduationCap, Smartphone } from 'lucide-react';
import { useLandingAuth } from '../../contexts/LandingAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const { user, profile, signOut } = useLandingAuth();
  const { checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isUserMenuOpen && !e.target.closest('.user-menu-container')) setIsUserMenuOpen(false);
      if (showMore && !e.target.closest('.more-menu-container')) setShowMore(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen, showMore]);

  // Streamlined: all "Sign In" / "Get Started" entries on the marketing
  // page now go through the dedicated /login route (LoginPage.js) — single
  // canonical sign-in surface. The legacy AuthModal is kept as a component
  // but no longer mounted from the navbar, so users never see two distinct
  // sign-in screens in a row.
  const goToSignIn = () => {
    setIsMobileMenuOpen(false);
    navigate('/login');
  };
  const goToSignUp = () => {
    setIsMobileMenuOpen(false);
    navigate('/login?mode=signup');
  };

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
  };

  const handleGoToApp = async () => {
    setIsUserMenuOpen(false);
    await checkAuth();
    navigate('/app');
  };

  const primaryNav = [
    { label: 'Features', href: '/#features', icon: null },
    { label: 'Pricing', href: '/pricing', icon: null },
    { label: 'For Educators', href: '/educators', icon: null },
    { label: 'Mobile', href: '/mobile', icon: Smartphone },
  ];

  const moreLinks = [
    { label: 'Dashboard', href: '/app', icon: LayoutDashboard },
    { label: 'Lectures', href: '/lectures', icon: Mic },
    { label: 'Marketplace', href: '/marketplace', icon: ShoppingBag },
    { label: 'Exam Mode', href: '/exam-mode', icon: GraduationCap },
    { label: 'Study Rooms', href: '/study-rooms', icon: BookOpen },
    { label: 'Tutors', href: '/tutors', icon: User },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[var(--isp-page)]/95 backdrop-blur-xl shadow-[0_1px_0_#E4E4E7]'
            : 'bg-transparent'
        }`}
        style={{ fontFamily: 'var(--sf-text)' }}
        data-testid="landing-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
              <img src="/logo-main.png" alt="iStudyPro" className="w-7 h-7 object-contain" />
              <span className="text-lg font-bold tracking-tight hidden sm:block" style={{ fontFamily: 'var(--sf-display)' }}>
                <span className="text-[var(--isp-heading)]">iStudy</span>
                <span className="text-[#002FA7]">Pro</span>
              </span>
            </Link>

            {/* Primary Nav — Desktop */}
            <div className="hidden md:flex items-center gap-0.5">
              {primaryNav.map((item) => {
                const isAnchor = item.href.includes('#');
                const handleAnchorClick = (e) => {
                  const targetId = item.href.split('#')[1];
                  if (window.location.pathname === '/' && targetId) {
                    e.preventDefault();
                    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
                  }
                };
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={isAnchor ? handleAnchorClick : undefined}
                    className="px-4 py-2 text-[13px] font-medium text-[var(--isp-body)] hover:text-[var(--isp-heading)] tracking-tight transition-colors"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {/* App dropdown — only for authed users; anonymous see a single
                  "See it in action" CTA scrolling to features instead. */}
              {user ? (
                <div className="relative more-menu-container">
                  <button
                    onClick={() => setShowMore(!showMore)}
                    className="flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-[var(--isp-body)] hover:text-[var(--isp-heading)] tracking-tight transition-colors"
                    data-testid="nav-more"
                  >
                    App
                    <ChevronDown className={`w-3 h-3 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                  </button>

                  {showMore && (
                    <div className="absolute top-full right-0 mt-2 w-52 bg-[var(--isp-page)] border border-[var(--isp-glass-border)] shadow-xl py-2" data-testid="more-dropdown">
                      {moreLinks.map((link) => {
                        const Icon = link.icon;
                        return (
                          <Link
                            key={link.label}
                            to={link.href}
                            onClick={() => setShowMore(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] transition-colors"
                          >
                            {Icon && <Icon className="w-4 h-4 text-[var(--isp-muted)]" />}
                            {link.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/#features"
                  onClick={(e) => {
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-[13px] font-medium text-[#002FA7] hover:text-[#00227A] tracking-tight transition-colors"
                  data-testid="nav-see-in-action"
                >
                  See it in action →
                </Link>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-[var(--isp-muted)] hover:text-[var(--isp-heading)] transition-colors"
                data-testid="theme-toggle-nav"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
              </button>

              {user ? (
                <div className="relative user-menu-container">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-[var(--isp-glass-border)] hover:border-[#A1A1AA] transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-6 h-6 bg-[#002FA7] flex items-center justify-center text-white font-semibold text-[10px]">
                      {(profile?.fullName || user.email || 'U')[0].toUpperCase()}
                    </div>
                    <span className="text-[13px] font-medium text-[var(--isp-heading)] hidden sm:block">
                      {profile?.fullName?.split(' ')[0] || 'Account'}
                    </span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-[var(--isp-page)] border border-[var(--isp-glass-border)] shadow-xl py-2" data-testid="user-dropdown-menu">
                      <div className="px-4 py-3 border-b border-[var(--isp-glass-border)]">
                        <p className="text-[var(--isp-heading)] font-medium truncate text-sm">{profile?.fullName || 'User'}</p>
                        <p className="text-[var(--isp-muted)] text-xs truncate">{user.email}</p>
                      </div>
                      <button onClick={handleGoToApp} className="w-full text-left px-4 py-2.5 text-[13px] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4 text-[var(--isp-muted)]" /> Dashboard
                      </button>
                      <button onClick={() => { navigate('/settings'); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-[13px] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] flex items-center gap-2">
                        <Settings className="w-4 h-4 text-[var(--isp-muted)]" /> Settings
                      </button>
                      <div className="border-t border-[var(--isp-glass-border)] mt-1 pt-1">
                        <button onClick={handleSignOut} className="w-full text-left px-4 py-2.5 text-[13px] text-[#FF3B30] hover:bg-red-50 flex items-center gap-2">
                          <LogOut className="w-4 h-4" /> Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToSignIn}
                    className="text-[13px] font-medium text-[var(--isp-body)] hover:text-[var(--isp-heading)] px-3 py-2 transition-colors hidden sm:block tracking-tight"
                    data-testid="navbar-signin-btn"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={goToSignUp}
                    className="bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold px-5 py-2 text-[13px] tracking-tight transition-colors"
                    data-testid="navbar-get-started-btn"
                  >
                    Get Started
                  </button>
                </div>
              )}

              {/* Mobile menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-[var(--isp-body)] hover:text-[var(--isp-heading)]"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[var(--isp-page)] border-t border-[var(--isp-glass-border)] px-4 py-4" data-testid="mobile-menu">
            <div className="space-y-1">
              {primaryNav.map((item) => (
                <Link key={item.label} to={item.href} onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-[13px] font-medium text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] transition-colors">
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-[var(--isp-glass-border)] pt-2 mt-2">
                {user ? (
                  <>
                    <p className="px-4 py-1 text-[10px] font-semibold text-[var(--isp-muted)] uppercase tracking-widest">App</p>
                    {moreLinks.map((link) => (
                      <Link key={link.label} to={link.href} onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-4 py-2.5 text-[13px] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] transition-colors">
                        {link.label}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    to="/#features"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="block px-4 py-3 text-[13px] font-semibold text-[#002FA7] hover:bg-[var(--isp-glass-inner)] transition-colors"
                    data-testid="mobile-see-in-action"
                  >
                    See it in action →
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
